/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import type { IsLoggedModel } from '@veeam-vspc/models/web-controllers';
import type { IdentityProvider } from '@veeam-vspc/models/rest';

import { LoginSupplier } from '../../suppliers/web';
import { AuthenticationSupplier } from '../../suppliers/rest';

import type { RequestSuccessResponse, RequestErrorResponse } from '../../interfaces';
import type { AppDataStoreParams } from '../../stores/app-data';
import type { PortalDataRequestOptions, PortalServiceConfig } from './interfaces';

export class PortalService {

    protected readonly loginSupplier: LoginSupplier;
    protected readonly authenticationSupplier: AuthenticationSupplier;

    protected readonly loginDataUrl: string;
    protected readonly identityProvidersUrl: string | void;

    constructor(portalServiceConfig: PortalServiceConfig<RequestErrorResponse | Error>) {
        const {
            transportService,
            loginDataUrl,
            identityProvidersUrl,
        } = portalServiceConfig;

        this.loginDataUrl = loginDataUrl;
        this.identityProvidersUrl = identityProvidersUrl;

        this.loginSupplier = new LoginSupplier(transportService);
        this.authenticationSupplier = new AuthenticationSupplier(transportService);
    }

    getLoginData(options: PortalDataRequestOptions = {}): Promise<AppDataStoreParams> {
        return this.getAllAppDataStore(options)
            .then(([loginData, identityProviders]) => ({
                loginData,
                identityProviders,
            }));
    }

    protected getAllAppDataStore({ noAuth = false }: PortalDataRequestOptions): Promise<[IsLoggedModel, IdentityProvider[] | null]> {
        return Promise.all([
            this.loadLoginData(noAuth),
            this.getIdentityProviders(noAuth),
        ]);
    }

    protected loadLoginData(noAuth: boolean) {
        return this.loginSupplier.isLogged(this.loginDataUrl, { disableGlobalFilters: true }, { noAuth })
            .then((resp: RequestSuccessResponse<IsLoggedModel>) => resp.data);
    }

    protected getIdentityProviders(noAuth: boolean) {
        return (noAuth && this.identityProvidersUrl)
            ? this.authenticationSupplier.getIdentityProviders(this.identityProvidersUrl, { disableGlobalFilters: true }, { noAuth })
                .then((resp: RequestSuccessResponse<IdentityProvider[]>) => resp.data)
            : Promise.resolve(null);
    }

}
